<template>
	<div id="Privacy" class="fullSize fullPageStatic">

        <div class="fullPageStatic_close" v-on:click="close"></div>

		<div class="scrollable_content_fullpage fullSize" >

<h1 class="title_bold">Privacy Statement</h1>

<h2 class="subtitle">Introduction</h2>
<p>Stryker recognises that the personal information it receives is held in a position of trust. Stryker seeks to fulfill that trust by adhering to general principles regarding the protection of personal information. This Privacy Statement explains how we collect, use, share and protect information in the course of operating our business.</p>

<h2 class="subtitle">Scope</h2>
<p>This Privacy Statement ("Statement") applies to the personal information of consumers that is collected or used by Stryker, its affiliates or subsidiaries (collectively, "Stryker", "we", "our", "us"). This Statement applies to all the personal information that Stryker collects when consumers interact with us, such as when visiting our websites, using or purchasing our products or services, contacting customer service and when interacting with us as a business customer, supplier or business partner (collectively, the "Services").</p>

<p>This includes, without limitation, all online and offline collections of all types of personal information. However, some Stryker collections involve types of data with special requirements (for example, health information) which require a different privacy notice. Whenever that is the case, Stryker will make it clear that the privacy notice concerned is different from this Statement.</p>

<h2 class="subtitle">Information collection</h2>

<p>Stryker may ask you to provide personal information for purposes including, but not limited to, the following:</p>
<p>
    <ul>
        <li>Buying Stryker products and services;</li>
        <li>Activating or registering certain products and services or enhancing functionality;</li>
        <li>Receiving information about Stryker products and services;</li>
        <li>Participating in Stryker online communities, including our social media channels/pages; </li>
        <li>Storing your preferences for future interactions and communications from Stryker; </li>
        <li>Helping us to develop products and services and create campaigns that are designed around you, optimise customer services and continuously improve our websites;</li>
        <li>Helping us to improve products and services, and allowing Stryker to keep you informed of, or involve you in the testing of, new products and services;</li>
        <li>Resolving consumer and/or product and services issues;</li>
        <li>Registering visitors of Stryker facilities or Stryker organised events and conferences;</li>
        <li>Contract or tender management; and </li>
        <li>Receiving personalised messages, special offers and advertisements that are relevant to your personal interests, based on the information you have shared with us and on the information we have collected through cookies or similar techniques regarding your use of the Stryker websites/social media/blogs.</li>
    </ul>
</p>

<p>
    Stryker maintains physical, technical, and administrative safeguards to protect your personal information and only allows disclosures as permitted by law to assist in providing products or services. We may also collect product and service information and provide these statistics to others in an aggregate form where the information has been de-identified.  
</p>

<p>Personal information collected may include:</p>

<p>
    <ul>
        <li>Contact information, such as name, address, email, telephone number, fax number, organisation name, and/or job title;</li>
        <li>Unique identifiers and preference information such as username, password, marketing preferences, internet protocol (IP) address, browser type, operating system, computer or mobile device, or navigation and clickstream behaviour for online interactions;</li>
        <li>Resume or CV, including work history, professional qualifications, publications, awards, references, completed trainings, and signature;</li>
        <li>Food restrictions, passport info for travel bookings or identification purposes, social security number (where required by law), bank account details, (emergency) contact persons, family information (where applicable and in accordance with local law);</li>
        <li>Creditworthiness, VAT number, product, purchase information history, request documentation;</li>
        <li>Pictures, video and audio recordings where you have provided your permission;</li>
        <li>Sensitive personal information, such as health information or reports of an individual end-customer (e.g.,concerning product claims and investigations, patient pre- and post-operative outcomes for research & statistical purposes, or criminal records for due diligence procedures in accordance with applicable law).</li>
    </ul>
</p>

<p>
    Stryker will retain and use the personal information collected for above mentioned processes and processing purposes in accordance with legal obligations. 
</p>

<h2 class="subtitle">Legal Bases</h2>

<p>
    The legal basis Stryker uses to process personal information will be dependent on the processing purposes and the jurisdictional legal requirements. Where the personal information collected involved is of EU citizens, in general, we use the following basis:
</p>

<p>
    <ul>
        <li>Consent to use the personal information, e.g. where this involves (digital) direct marketing activities and where we would like to use a photo, video or audio recording with identifiable individuals for external publishing; </li>
        <li>Establishment or performance of a contract, e.g. where we enter into a contract with a party and we collect contact information and signatures, as well as potential bank account details that may be linked to a representative of a company we do business with; </li>
        <li>Compliance with a legal obligation, e.g. where we need to disclose consumer details for compliance with (local or industry) laws such as the Physician Payments Sunshine Act or for compliance with the Medical Device Regulation; </li>
        <li>Legitimate interest of Stryker, e.g. where Stryker collects contact information and preferences, details for lodging and travel for attendees of Stryker organised events & trainings or third party hosted events & trainings in collaboration with Stryker;</li>
        <li>Public interest or for the exercise of public authority in strictly limited cases, e.g.. where we would be required to cooperate with the police or other government bodies in case of illegal conduct;</li>
        <li>For the establishment, exercise or defence of legal claims, e.g. for court cases;</li>
        <li>For scientific or research purposes, e.g. for clinical research studies with sufficient safeguards safeguarding confidentiality;</li>
        <li>For preventative and occupational medicine, medical diagnosis pursuant to contract with healthcare professionals;</li>
        <li>For public interest in the public health to ensure high standards of quality and safety of healthcare and products; and</li>
        <li>Where the individual chooses to disclose the personal information publicly by its own initiative. </li>
    </ul>
</p>

<p>If you choose not to provide the personal information we reasonably require, it may hinder Stryker’s ability to provide the information or services you have requested.</p>

<h2 class="subtitle">Cross-Border Transfers</h2>
<p>
    To the extent personal information is transferred out of the country where the owner of that personal information lives, such as to Stryker affiliates or business partners in other countries, including in the United States, different standards may apply to how your data is used and protected in those countries. Stryker has appropriate safeguards in place in accordance with applicable legal requirements to ensure that data is adequately protected irrespective of the country. This includes obtaining written assurances from any third party given access to your data so as to require them to adopt standards that ensure an equivalent level of protection for data as that adopted by Stryker and standardized corporate safeguards and contractual measures (based on the European Commission Model Clauses) for internal data transfers to Stryker affiliates in third countries which are deemed to provide an inadequate level of data protection.
</p>

<h2 class="subtitle">Privacy Statement for Children</h2>
<p>
    Stryker will not collect personal information from anyone we know to be under the age of 16 without the prior, verifiable consent from his or her legal representative. Such legal representative has the right, upon request, to view the information provided by the child and/or to require that it be deleted. 
</p>

<h2 class="subtitle">Retention of Information</h2>
<p>
    Stryker will retain your personal information for as long as reasonably necessary to comply with legal obligations or for no longer as required for legitimate business purposes.
</p>

<h2 class="subtitle">Disclosure of Information</h2>
<p>
    Stryker may share personal information we have collected with companies or agents doing technological maintenance or working on our behalf to help fulfil business needs, including providing customer services and distributing marketing communications. Stryker may also share personal information with our subsidiaries and affiliates.
</p>

<p>
    Other service providers that may be used to perform certain functions on our behalf and to whom personal information may be disclosed in order to perform their intended function include call-centre support, sending or processing postal or electronic mail or analysing or hosting information on cloud-based servers. 
</p>

<p>
    Stryker does not share your personal information with unaffiliated third parties for their own direct marketing purposes.
</p>

<h2 class="subtitle">Security</h2>
<p>
    The security and confidentiality of your personal information matters to us. For this reason, Stryker has physical, technical and administrative controls in place to protect your personal information from unauthorised access, use and disclosure. Stryker evaluates these safeguards on an ongoing basis to help minimise risks from new security threats as they become known.
</p>

<h2 class="subtitle">Rights</h2>
<p>
    You may request details of personal information which we hold about you. If you believe that any personal information we are holding about you is incorrect or incomplete, please contact us as soon as possible, at the address below. We will promptly correct any personal information found to be incorrect.
</p>

<p>
    You may always choose to object to the collection or use of your personal information or to have your information erased. If you would like a copy of the information held about you for your own use or to transfer to another party, or if you wish to exercise any other right, please contact us at <a href="mailto:globalprivacy@stryker.com">globalprivacy@stryker.com</a>.
</p>

<h2 class="subtitle">Updates to Privacy Statement</h2>

<p>
    This Statement may be amended at any time. If material changes are made in how personal information is collected, used, disclosed or otherwise processed, this Statement will be updated and notices will be provided when/where appropriate. Any material changes to this Statement will be effective at the time of our posting of the notice of the update. Where required to do so by law, Stryker may seek your prior consent to any material changes we make to this Privacy Statement.
</p>

<p>
    The Statement was last updated on [23-04-2018]
</p>

<h2 class="subtitle">Contact</h2>
<p>
    If you have any questions, concerns or comments about this Statement, please contact us. Stryker will use reasonable efforts to respond to you as soon as possible.
</p>

<h3>Send mail to:</h3>
<p>
    Stryker Corporation<br />
    Attn.: Data Privacy Director<br />
    2825 Airview Boulevard<br />
    Kalamazoo, MI 49002<br />
</p>

<h3>Send mail to:</h3>
<p>
    <a href="mailto:globalprivacy@stryker.com">globalprivacy@stryker.com</a>
</p>


        </div>
	</div>
</template>

<script>
export default {
	name: 'Privacy',
	props: {
		App: Object
	},
	data : function() {
		return {
			
		}
	},
	mounted() {
		
	},
	created() {
	},
	methods : {

        close() {
            this.App.privacyPageDisplay = false;
        }

	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
